/**
 * Formateadores para Colombia (es-CO).
 * Moneda: COP con punto miles y coma decimales.
 */

const moneyFormatter = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("es-CO", {
  maximumFractionDigits: 0,
});

const dateFormatter = new Intl.DateTimeFormat("es-CO", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const dateTimeFormatter = new Intl.DateTimeFormat("es-CO", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** Formato moneda: "$ 1.234.567" */
export function formatMoney(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "$ 0";
  return moneyFormatter.format(value);
}

/** Formato número con miles: "1.234.567" */
export function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "0";
  return numberFormatter.format(value);
}

/** Formato fecha: "5 may 2026" */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "";
  const date = typeof value === "string" ? new Date(value) : value;
  return dateFormatter.format(date);
}

/** Formato fecha + hora: "5 may 2026, 10:32" */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "";
  const date = typeof value === "string" ? new Date(value) : value;
  return dateTimeFormatter.format(date);
}

/** Tiempo relativo: "hace 5 min", "hace 2 h", "ayer", "hace 3 días" */
export function formatRelativeTime(value: string | Date | null | undefined): string {
  if (!value) return "";
  const date = typeof value === "string" ? new Date(value) : value;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  const diffH = Math.round(diffMs / 3600000);
  const diffD = Math.round(diffMs / 86400000);

  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffH < 24) return `hace ${diffH} h`;
  if (diffD === 1) return "ayer";
  if (diffD < 7) return `hace ${diffD} días`;
  return formatDate(date);
}

/** Parsea string de moneda a número: "$1.234.567" → 1234567 */
export function parseMoney(value: string): number {
  if (!value) return 0;
  const cleaned = value.replace(/[^\d]/g, "");
  return Number.parseInt(cleaned, 10) || 0;
}

/** Valida documento colombiano (CC: 6-10 dígitos, CE: 6-10) */
export function validateDocumento(doc: string): boolean {
  if (!doc) return false;
  const cleaned = doc.replace(/\D/g, "");
  return cleaned.length >= 6 && cleaned.length <= 10;
}

/** Valida IMEI con checksum Luhn (15 dígitos) */
export function validateImei(imei: string): boolean {
  if (!imei) return false;
  const cleaned = imei.replace(/\D/g, "");
  if (cleaned.length !== 15) return false;

  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let digit = Number.parseInt(cleaned[i], 10);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

/** Valida VIN (17 caracteres alfanuméricos, sin I/O/Q) */
export function validateVin(vin: string): boolean {
  if (!vin) return false;
  return /^[A-HJ-NPR-Z0-9]{17}$/i.test(vin);
}
